import React, { useState, useEffect } from 'react';
import { technicalAnalysisService, RSIData, MACDData, BollingerBandsData, MovingAverageData, VolumeData } from '../services/technicalAnalysis';

interface TechnicalAnalysisPanelProps {
  symbol: string;
  onClose: () => void;
}

const TechnicalAnalysisPanel = ({ symbol, onClose }: TechnicalAnalysisPanelProps) => {
  const [indicators, setIndicators] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'rsi' | 'macd' | 'bollinger' | 'moving' | 'volume'>('rsi');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [selectedSymbol, setSelectedSymbol] = useState(symbol);
  const [isSwitching, setIsSwitching] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  // Available coins for analysis
  const availableCoins = [
    { symbol: 'BTCUSDT', name: 'Bitcoin', icon: '₿' },
    { symbol: 'ETHUSDT', name: 'Ethereum', icon: 'Ξ' },
    { symbol: 'SOLUSDT', name: 'Solana', icon: '◎' },
    { symbol: 'ADAUSDT', name: 'Cardano', icon: '₳' },
    { symbol: 'BNBUSDT', name: 'BNB', icon: '🟡' },
    { symbol: 'XRPUSDT', name: 'XRP', icon: '💧' },
    { symbol: 'DOGEUSDT', name: 'Dogecoin', icon: '🐕' },
    { symbol: 'AVAXUSDT', name: 'Avalanche', icon: '🔺' },
    { symbol: 'DOTUSDT', name: 'Polkadot', icon: '🔴' },
    { symbol: 'LINKUSDT', name: 'Chainlink', icon: '🔗' }
  ];

  useEffect(() => {
    const loadIndicators = async () => {
      setLoading(true);
      setIsSwitching(true);
      try {
        const data = await technicalAnalysisService.getAllIndicators(selectedSymbol);
        setIndicators(data);
        setLastUpdated(new Date());
      } catch (error) {
        console.error('Failed to load technical indicators:', error);
      } finally {
        setLoading(false);
        setIsSwitching(false);
      }
    };

    loadIndicators();
  }, [selectedSymbol]);

  const formatValue = (value: number, decimals: number = 2) => {
    return value.toFixed(decimals);
  };

  // Generate trading suggestions based on indicator values
  const getTradingSuggestion = () => {
    if (!indicators) return { suggestion: 'HOLD', confidence: 0, reasoning: 'No data available' };

    const rsi = indicators.rsi[indicators.rsi.length - 1];
    const macd = indicators.macd[indicators.macd.length - 1];
    const bollinger = indicators.bollinger[indicators.bollinger.length - 1];
    const volume = indicators.volume[indicators.volume.length - 1];

    let buySignals = 0;
    let sellSignals = 0;
    let reasoning: string[] = [];

    // RSI Analysis
    if (rsi.value < 30) {
      buySignals++;
      reasoning.push('RSI shows oversold (good buy opportunity)');
    } else if (rsi.value > 70) {
      sellSignals++;
      reasoning.push('RSI shows overbought (consider selling)');
    }

    // MACD Analysis
    if (macd.macd > macd.signal && macd.histogram > 0) {
      buySignals++;
      reasoning.push('MACD shows bullish momentum');
    } else if (macd.macd < macd.signal && macd.histogram < 0) {
      sellSignals++;
      reasoning.push('MACD shows bearish momentum');
    }

    // Bollinger Bands Analysis
    if (bollinger.position === 'below_lower') {
      buySignals++;
      reasoning.push('Price below lower Bollinger Band (oversold)');
    } else if (bollinger.position === 'above_upper') {
      sellSignals++;
      reasoning.push('Price above upper Bollinger Band (overbought)');
    }

    // Volume Analysis
    if (volume.signal === 'buy') {
      buySignals++;
      reasoning.push('High volume confirms price movement');
    } else if (volume.signal === 'sell') {
      sellSignals++;
      reasoning.push('Low volume suggests weak price movement');
    }

    // Moving Averages Analysis
    const sma20 = indicators.sma20[indicators.sma20.length - 1];
    const sma50 = indicators.sma50[indicators.sma50.length - 1];
    const ema12 = indicators.ema12[indicators.ema12.length - 1];
    const ema26 = indicators.ema26[indicators.ema26.length - 1];

    if (sma20 && sma50 && sma20.value > sma50.value) {
      buySignals++;
      reasoning.push('Short-term MA above long-term MA (uptrend)');
    } else if (sma20 && sma50 && sma20.value < sma50.value) {
      sellSignals++;
      reasoning.push('Short-term MA below long-term MA (downtrend)');
    }

    if (ema12 && ema26 && ema12.value > ema26.value) {
      buySignals++;
      reasoning.push('EMA 12 above EMA 26 (bullish trend)');
    } else if (ema12 && ema26 && ema12.value < ema26.value) {
      sellSignals++;
      reasoning.push('EMA 12 below EMA 26 (bearish trend)');
    }

    // Determine overall suggestion
    const totalSignals = buySignals + sellSignals;
    const confidence = totalSignals > 0 ? Math.round((Math.max(buySignals, sellSignals) / totalSignals) * 100) : 0;

    let suggestion: 'BUY' | 'SELL' | 'HOLD';
    if (buySignals > sellSignals && buySignals >= 2) {
      suggestion = 'BUY';
    } else if (sellSignals > buySignals && sellSignals >= 2) {
      suggestion = 'SELL';
    } else {
      suggestion = 'HOLD';
    }

    return {
      suggestion,
      confidence,
      reasoning: reasoning.length > 0 ? reasoning : ['Mixed signals - no clear direction'],
      buySignals,
      sellSignals
    };
  };

  // Indicator explanations for beginners
  const indicatorExplanations = {
    rsi: {
      title: "RSI (Relative Strength Index)",
      description: "Measures if a coin is overbought (expensive) or oversold (cheap)",
      interpretation: {
        overbought: "Above 70: Coin might be overpriced, consider selling",
        oversold: "Below 30: Coin might be underpriced, consider buying",
        neutral: "30-70: Normal price range, no clear signal"
      },
      howToRead: "Higher numbers = more expensive, Lower numbers = cheaper"
    },
    macd: {
      title: "MACD (Moving Average Convergence Divergence)",
      description: "Shows momentum and trend changes",
      interpretation: {
        bullish: "MACD line above signal line: Upward trend",
        bearish: "MACD line below signal line: Downward trend",
        crossover: "Lines crossing: Potential trend change"
      },
      howToRead: "Blue line above red = good, Blue line below red = caution"
    },
    bollinger: {
      title: "Bollinger Bands",
      description: "Shows price volatility and potential support/resistance levels",
      interpretation: {
        upper: "Price near upper band: Might be overbought",
        lower: "Price near lower band: Might be oversold",
        squeeze: "Bands close together: Low volatility, big move coming"
      },
      howToRead: "Price bouncing between bands = normal, Price outside bands = extreme"
    },
    moving: {
      title: "Moving Averages",
      description: "Shows average price over time to identify trends",
      interpretation: {
        golden: "Short MA above Long MA: Uptrend",
        death: "Short MA below Long MA: Downtrend",
        support: "Price above MA: Support level",
        resistance: "Price below MA: Resistance level"
      },
      howToRead: "Price above lines = uptrend, Price below lines = downtrend"
    },
    volume: {
      title: "Volume Analysis",
      description: "Shows how much trading activity is happening",
      interpretation: {
        high: "High volume: Strong interest, confirms price moves",
        low: "Low volume: Weak interest, price moves less reliable",
        breakout: "High volume + price move: Strong signal"
      },
      howToRead: "More volume = more reliable price moves"
    }
  };

  const getSignalColor = (signal?: string) => {
    switch (signal) {
      case 'buy': return '#10b981';
      case 'sell': return '#ef4444';
      case 'high': return '#f59e0b';
      case 'low': return '#6b7280';
      default: return '#64748b';
    }
  };

  const getSignalText = (signal?: string) => {
    switch (signal) {
      case 'buy': return 'BUY';
      case 'sell': return 'SELL';
      case 'high': return 'HIGH';
      case 'low': return 'LOW';
      default: return 'NEUTRAL';
    }
  };

  if (loading) {
    return (
      <div className="technical-analysis-panel">
        <div className="ta-header">
          <h3>Technical Analysis - {symbol}</h3>
          <button className="close-btn" onClick={onClose}>✖</button>
        </div>
        <div className="ta-loading">Loading indicators...</div>
      </div>
    );
  }

  if (!indicators) {
    return (
      <div className="technical-analysis-panel">
        <div className="ta-header">
          <h3>Technical Analysis - {symbol}</h3>
          <button className="close-btn" onClick={onClose}>✖</button>
        </div>
        <div className="ta-error">Failed to load indicators</div>
      </div>
    );
  }

  return (
    <div className="technical-analysis-panel">
      <div className="ta-header">
        <div className="ta-header-left">
          <div className="symbol-selector">
            <label htmlFor="coin-select">Select Coin:</label>
            <select
              id="coin-select"
              value={selectedSymbol}
              onChange={(e) => setSelectedSymbol(e.target.value)}
              className="coin-select"
              disabled={loading}
            >
              {availableCoins.map(coin => (
                <option key={coin.symbol} value={coin.symbol}>
                  {coin.icon} {coin.name} ({coin.symbol})
                </option>
              ))}
            </select>
            <div className="quick-coins">
              {availableCoins.slice(0, 5).map(coin => (
                <button
                  key={coin.symbol}
                  className={`quick-coin-btn ${selectedSymbol === coin.symbol ? 'active' : ''}`}
                  onClick={() => setSelectedSymbol(coin.symbol)}
                  disabled={loading}
                  title={`Switch to ${coin.name}`}
                >
                  {coin.icon}
                </button>
              ))}
            </div>
          </div>
          {isSwitching ? (
            <div className="switching-indicator">
              🔄 Switching to {selectedSymbol}...
            </div>
          ) : lastUpdated ? (
            <div className="last-updated">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </div>
          ) : null}
        </div>
        <div className="ta-header-right">
          <button 
            className="help-btn" 
            onClick={() => setShowHelp(!showHelp)}
            title="Learn about indicators"
          >
            ❓
          </button>
          <button 
            className="refresh-btn" 
            onClick={() => {
              setLoading(true);
              technicalAnalysisService.getAllIndicators(selectedSymbol).then(data => {
                setIndicators(data);
                setLastUpdated(new Date());
                setLoading(false);
              });
            }}
            disabled={loading}
            title="Refresh Data"
          >
            {loading ? '⏳' : '🔄'}
          </button>
          <button className="close-btn" onClick={onClose}>✖</button>
        </div>
      </div>

      {showHelp && (
        <div className="help-panel">
          <div className="help-content">
            <h4>📚 Technical Analysis Guide for Beginners</h4>
            <div className="help-sections">
              {Object.entries(indicatorExplanations).map(([key, explanation]) => (
                <div key={key} className="help-section">
                  <h5>{explanation.title}</h5>
                  <p className="help-description">{explanation.description}</p>
                  <div className="help-interpretation">
                    <strong>How to interpret:</strong>
                    <ul>
                      {Object.entries(explanation.interpretation).map(([interpretationKey, text]) => (
                        <li key={interpretationKey}>{text}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="help-simple">
                    <strong>Simple rule:</strong> {explanation.howToRead}
                  </div>
                </div>
              ))}
            </div>
            <div className="help-tips">
              <h5>💡 Pro Tips:</h5>
              <ul>
                <li>Never rely on just one indicator - use multiple together</li>
                <li>Look for confirmation from 2-3 indicators before making decisions</li>
                <li>Consider market conditions and news alongside technical analysis</li>
                <li>Practice with paper trading before using real money</li>
                <li>Technical analysis works better in trending markets</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="ta-tabs">
        <button 
          className={`ta-tab ${activeTab === 'rsi' ? 'active' : ''}`}
          onClick={() => setActiveTab('rsi')}
          title="RSI: Measures if coin is overbought (expensive) or oversold (cheap)"
        >
          RSI
        </button>
        <button 
          className={`ta-tab ${activeTab === 'macd' ? 'active' : ''}`}
          onClick={() => setActiveTab('macd')}
          title="MACD: Shows momentum and trend changes"
        >
          MACD
        </button>
        <button 
          className={`ta-tab ${activeTab === 'bollinger' ? 'active' : ''}`}
          onClick={() => setActiveTab('bollinger')}
          title="Bollinger Bands: Shows price volatility and support/resistance levels"
        >
          Bollinger
        </button>
        <button 
          className={`ta-tab ${activeTab === 'moving' ? 'active' : ''}`}
          onClick={() => setActiveTab('moving')}
          title="Moving Averages: Shows average price over time to identify trends"
        >
          Moving Avg
        </button>
        <button 
          className={`ta-tab ${activeTab === 'volume' ? 'active' : ''}`}
          onClick={() => setActiveTab('volume')}
          title="Volume: Shows trading activity - more volume = more reliable price moves"
        >
          Volume
        </button>
      </div>

      <div className="ta-content">
        {/* Trading Suggestion Panel */}
        {indicators && (
          <div className="trading-suggestion-panel">
            {(() => {
              const suggestion = getTradingSuggestion();
              return (
                <div className={`suggestion-card ${suggestion.suggestion.toLowerCase()}`}>
                  <div className="suggestion-header">
                    <div className="suggestion-main">
                      <span className="suggestion-icon">
                        {suggestion.suggestion === 'BUY' ? '📈' : suggestion.suggestion === 'SELL' ? '📉' : '⏸️'}
                      </span>
                      <div className="suggestion-text">
                        <h3>{suggestion.suggestion}</h3>
                        <p>{suggestion.confidence}% confidence</p>
                      </div>
                    </div>
                    <div className="suggestion-stats">
                      <span className="buy-signals">🟢 {suggestion.buySignals}</span>
                      <span className="sell-signals">🔴 {suggestion.sellSignals}</span>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {activeTab === 'rsi' && (
          <div className="indicator-section">
            <h4>RSI (Relative Strength Index)</h4>
            <div className="indicator-grid">
              {indicators.rsi.slice(-5).map((rsi: RSIData, index: number) => (
                <div key={index} className="indicator-item">
                  <div className="indicator-value">
                    <span className="value">{formatValue(rsi.value)}</span>
                    <span 
                      className="signal" 
                      style={{ color: getSignalColor(rsi.signal) }}
                    >
                      {getSignalText(rsi.signal)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'macd' && (
          <div className="indicator-section">
            <h4>MACD (Moving Average Convergence Divergence)</h4>
            <div className="indicator-grid">
              {indicators.macd.slice(-5).map((macd: MACDData, index: number) => (
                <div key={index} className="indicator-item">
                  <div className="indicator-value">
                    <span className="value">MACD: {formatValue(macd.macd)}</span>
                    <span 
                      className="signal" 
                      style={{ color: getSignalColor(macd.signal_type) }}
                    >
                      {getSignalText(macd.signal_type)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'bollinger' && (
          <div className="indicator-section">
            <h4>Bollinger Bands</h4>
            <div className="indicator-grid">
              {indicators.bollinger.slice(-5).map((bb: BollingerBandsData, index: number) => (
                <div key={index} className="indicator-item">
                  <div className="indicator-value">
                    <span className="value">Upper: {formatValue(bb.upper)}</span>
                    <span className="signal" style={{ color: getSignalColor(bb.position) }}>
                      {bb.position?.replace('_', ' ').toUpperCase() || 'BETWEEN'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'moving' && (
          <div className="indicator-section">
            <h4>Moving Averages</h4>
            <div className="indicator-grid">
              <div className="indicator-item">
                <h5>SMA 20</h5>
                {indicators.sma20.slice(-3).map((sma: MovingAverageData, index: number) => (
                  <div key={index} className="ma-item">
                    <span className="value">{formatValue(sma.value)}</span>
                    <span className="period">SMA 20</span>
                  </div>
                ))}
              </div>
              <div className="indicator-item">
                <h5>SMA 50</h5>
                {indicators.sma50.slice(-3).map((sma: MovingAverageData, index: number) => (
                  <div key={index} className="ma-item">
                    <span className="value">{formatValue(sma.value)}</span>
                    <span className="period">SMA 50</span>
                  </div>
                ))}
              </div>
              <div className="indicator-item">
                <h5>EMA 12</h5>
                {indicators.ema12.slice(-3).map((ema: MovingAverageData, index: number) => (
                  <div key={index} className="ma-item">
                    <span className="value">{formatValue(ema.value)}</span>
                    <span className="period">EMA 12</span>
                  </div>
                ))}
              </div>
              <div className="indicator-item">
                <h5>EMA 26</h5>
                {indicators.ema26.slice(-3).map((ema: MovingAverageData, index: number) => (
                  <div key={index} className="ma-item">
                    <span className="value">{formatValue(ema.value)}</span>
                    <span className="period">EMA 26</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'volume' && (
          <div className="indicator-section">
            <h4>Volume Analysis</h4>
            <div className="indicator-grid">
              {indicators.volume.slice(-5).map((vol: VolumeData, index: number) => (
                <div key={index} className="indicator-item">
                  <div className="indicator-value">
                    <span className="value">Volume: {formatValue(vol.volume, 0)}</span>
                    <span 
                      className="signal" 
                      style={{ color: getSignalColor(vol.signal) }}
                    >
                      {getSignalText(vol.signal)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TechnicalAnalysisPanel;
